import { VARIABLE_REGEX } from './dialect.ts'
import type { Variables } from './types.ts'

/**
 * Tira o escape dos colchetes do índice (`clinic.witnesses\[1].name` → `clinic.witnesses[1].name`).
 * O Markdown salvo pelo editor escapa `[` em texto corrido; o caminho canônico não tem barra.
 */
export function normalizeVariablePath(path: string): string {
  return path.replace(/\\([[\]])/g, '$1')
}

/** Caminhos de variável usados no texto, sem repetição, na ordem em que aparecem. */
export function extractVariables(markdown: string): string[] {
  const seen = new Set<string>()
  for (const m of markdown.matchAll(VARIABLE_REGEX)) {
    const name = m[1] && normalizeVariablePath(m[1])
    if (name && !seen.has(name)) seen.add(name)
  }
  return [...seen]
}

/**
 * O valor de uma variável. `''` é valor (a opcional com texto vazio, como o complemento do endereço);
 * só ausente e nulo são "sem valor".
 */
export function readVariable(values: Variables | undefined, path: string): string | undefined {
  if (!values) return undefined
  const v = values[normalizeVariablePath(path)]
  if (v === undefined || v === null) return undefined
  return String(v)
}

/** A variável com o separador que a precede (", " ou espaço), para a vazia sumir sem deixar ", ,". */
const VARIABLE_WITH_SEPARATOR = new RegExp(`(,[ \\t]*|[ \\t])?${VARIABLE_REGEX.source}`, 'g')

/**
 * Substitui `{{path}}` pelos valores. O que não tem valor fica como está — para a etapa
 * de geração acusar `missing_variable_value` em vez de imprimir um espaço em branco no contrato.
 * A variável com valor vazio (opcional sem texto) some junto com a vírgula ou o espaço que a
 * precede: "Rua X, 10, {{complemento}}, Centro" vira "Rua X, 10, Centro".
 */
export function fillVariables(markdown: string, values: Variables): string {
  return markdown.replace(
    VARIABLE_WITH_SEPARATOR,
    (match, separator: string | undefined, path: string) => {
      const value = readVariable(values, path)
      if (value === undefined) return match
      if (value === '') return ''
      return `${separator ?? ''}${value}`
    },
  )
}

/**
 * O que o Markdown, o GFM e o dialeto leem como marcação em qualquer ponto da linha: escape,
 * código, ênfase, link, HTML, riscado, tabela, diretiva (`:`), entidade (`&`) e título.
 */
const INLINE_MARKUP = /[\\`*_[\]<>~|:&#]/g
/** No começo da linha: item de lista (`-`, `+`) e sublinhado de título (`=`, `-`). */
const LINE_START_MARKUP = /^([-+=])/
/** "1. " e "1) " no começo da linha abririam uma lista numerada. */
const ORDERED_LIST_MARKER = /^(\d+)([.)])/
/** Separa as chaves de `{{` sem mudar o que se lê: o texto nunca vira variável. */
const WORD_JOINER = '⁠'

function escapeLine(line: string): string {
  return line
    .replace(INLINE_MARKUP, '\\$&')
    .replace(/\{\{/g, `{${WORD_JOINER}{`)
    .replace(LINE_START_MARKUP, '\\$1')
    .replace(ORDERED_LIST_MARKER, '$1\\$2')
}

/**
 * Texto digitado (o texto livre da geração, `long_text`, RN-DOC-52) como Markdown que se lê igual
 * ao que foi escrito: cada caractere de marcação vai escapado, então nada vira negrito, lista,
 * título, link, diretiva ou variável. Linha em branco separa parágrafos; a quebra simples vira
 * quebra de linha; espaços nas pontas das linhas saem (quatro espaços no início seriam código).
 */
export function plainTextToMarkdown(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((paragraph) =>
      paragraph
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '')
        .map(escapeLine)
        .join('\\\n'),
    )
    .filter((paragraph) => paragraph !== '')
    .join('\n\n')
}
